import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Chip,
  Alert,
  FormHelperText,
  Grid,
  LinearProgress,
  SelectChangeEvent,
  Tooltip,
  FormControlLabel,
  Switch,
  InputAdornment,
  ButtonGroup,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Battery80,
  Battery50,
  Battery20,
  BatteryFull,
  BatteryAlert,
  BatteryUnknown
} from '@mui/icons-material';
import PoolWearableGatewaySettingsCard from './PoolWearableGatewaySettingsCard';
import PoolWearableBaselineDefaultsCard from './PoolWearableBaselineDefaultsCard';
import axios from 'axios';
import { attachAuthInterceptors } from '../../services/authTokenRefresh';
import { standardWearableApi } from '../../services/standardWearableApi';
import { API_ORIGIN } from '../../config';
import {
  WEARABLE_USAGE_PURPOSE_OPTIONS,
  DEFAULT_WEARABLE_USAGE_PURPOSE,
  normalizeWearableUsagePurpose,
  wearableUsagePurposeLabel,
} from '../../constants/wearableUsagePurpose';

// API URL 상수 정의
const API_URL = API_ORIGIN;

// axios 인스턴스 생성
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

attachAuthInterceptors(api);

// 요청 인터셉터 추가
api.interceptors.request.use(request => {
  console.log('API 요청 전송:', request);
  return request;
}, error => {
  console.error('API 요청 오류:', error);
  return Promise.reject(error);
});

// 응답 인터셉터 추가
api.interceptors.response.use(response => {
  console.log('API 응답 수신:', response);
  return response;
}, error => {
  console.error('API 응답 오류:', error);
  return Promise.reject(error);
});

// 상수 정의
const WEARABLE_TYPE_OPTIONS = [
  { value: 'wristband', label: '손목밴드' },
  { value: 'tag', label: '태그형' },
  { value: 'clip', label: '클립형' },
  { value: 'card', label: '카드형' },
  { value: 'watch', label: '시계형' }
];

const WEARABLE_STATUS_OPTIONS = [
  { value: 'active', label: '활성', enValue: 'active' },
  { value: 'inactive', label: '비활성', enValue: 'inactive' },
  { value: 'maintenance', label: '점검중', enValue: 'maintenance' },
  { value: 'lost', label: '분실', enValue: 'lost' },
  { value: 'error', label: '오류', enValue: 'error' }
];

interface Wearable {
  id: number;                 // 웨어러블 ID (primary key)
  pool_id: number;            // 시설 ID (foreign key)
  device_id: string;          // 장치 ID (API에서 제공)
  device_type: string;        // 장치 타입 (API에서 제공)
  battery_level: number;      // 배터리 레벨 (API에서 제공)
  status?: string;            // 상태 (API에서 제공되지 않을 수 있음)
  firmware_version?: string;  // 펌웨어 버전 (API에서 제공되지 않을 수 있음)
  is_active?: boolean;        // 활성화 여부 (API에서 제공되지 않을 수 있음)
  created_at: string;         // 생성일시 (API에서 제공)
  updated_at: string;         // 수정일시 (API에서 제공)
  name?: string;              // 장치 이름 (API에서 제공되지 않을 수 있음)
  last_battery_level?: number; // 마지막 배터리 레벨
  last_signal_strength?: number; // 마지막 신호 강도
  code?: string;              // 장치 코드 (API에서 제공되지 않을 수 있음)
  usage_purpose?: string;
}

interface WearableFormData {
  pool_id: number;
  device_id: string;          // 장치 ID (API에서 제공)
  device_type: string;        // 장치 타입
  battery_level: number;      // 배터리 레벨
  status: string;             // 상태 (UI 표시용)
  firmware_version: string;   // 펌웨어 버전 (UI 표시용)
  is_active: boolean;         // 활성화 여부 (UI 표시용)
  name?: string;              // 장치 이름
  last_battery_level?: number; // 마지막 배터리 레벨
  last_signal_strength?: number; // 마지막 신호 강도
  code?: string;              // 장치 코드 (UI 표시용)
  usage_purpose: string;
}

interface WearableManagementProps {
  poolId: number | string | undefined;
  poolCode: string | undefined;
  showSnackbar: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
  onRefreshData?: () => void;
}

// 유틸리티 함수
const getWearableTypeDisplay = (type: string | undefined) => {
  const typeOption = WEARABLE_TYPE_OPTIONS.find(option => option.value === type);
  return typeOption?.label || type || '-';
};

const getStatusColor = (status: string | undefined) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'inactive':
      return 'default';
    case 'maintenance':
      return 'warning';
    case 'lost':
      return 'error';
    case 'error':
      return 'error';
    default:
      return 'default';
  }
};

const getBatteryColor = (level: number | undefined) => {
  if (level === undefined) return 'inherit';
  if (level <= 20) return 'error';
  if (level <= 50) return 'warning';
  if (level <= 80) return 'info';
  return 'success';
};

const getStatusText = (status: string | number | null | undefined): string => {
  if (status === null || status === undefined) return '-';
  
  const statusStr = String(status);
  
  // 숫자 상태 코드 처리
  if (['0', '1', '2'].includes(statusStr)) {
    switch(statusStr) {
      case '0':
        return '오프라인';
      case '1':
        return '온라인';
      case '2':
        return '대기중';
    }
  }
  
  // 문자열 상태 처리
  switch(statusStr.toLowerCase()) {
    case 'active':
      return '활성';
    case 'inactive':
      return '비활성';
    case 'maintenance':
      return '점검중';
    case 'lost':
      return '분실';
    case 'error':
      return '오류';
    default:
      return statusStr;
  }
};

// Select 컴포넌트의 value로 사용할 상태값 변환 함수
const getStatusSelectValue = (status: string | number | null | undefined): string => {
  // status가 없으면 기본값 반환
  if (status === null || status === undefined) return 'active';
  
  // 현재 상태값 문자열로 변환 후 소문자화
  const currentStatus = String(status).toLowerCase();
  
  // 상태값에 맞는 옵션 찾기
  const matchedOption = WEARABLE_STATUS_OPTIONS.find(
    opt => opt.enValue.toLowerCase() === currentStatus
  );
  
  // 일치하는 옵션이 있으면 해당 값, 없으면 기본값 반환
  return matchedOption ? matchedOption.value : 'active';
};

const renderBatteryIcon = (batteryLevel: number | null | undefined) => {
  if (batteryLevel === null || batteryLevel === undefined) {
    return <BatteryUnknown color="disabled" />;
  }

  if (batteryLevel >= 75) {
    return <BatteryFull color="success" />;
  } else if (batteryLevel >= 50) {
    return <Battery80 color="success" />;
  } else if (batteryLevel >= 25) {
    return <Battery50 color="warning" />;
  } else if (batteryLevel >= 10) {
    return <Battery20 color="warning" />;
  } else {
    return <BatteryAlert color="error" />;
  }
};

const renderStatusChip = (status: string | number | null | undefined) => {
  if (status === null || status === undefined) {
    return <Chip label="알수없음" color="default" size="small" sx={{ minWidth: 70 }}/>;
  }
  
  let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" = "default";
  let label = String(status).toUpperCase() || "UNKNOWN";

  switch (label) {
    case 'ACTIVE':
      color = "success";
      label = '활성';
      break;
    case 'INACTIVE':
      color = "default";
      label = '비활성';
      break;
    case 'MAINTENANCE':
      color = "warning";
      label = '점검중';
      break;
    case 'LOST':
      color = "secondary";
      label = '분실';
      break;
    case 'ERROR':
      color = "error";
      label = '오류';
      break;
    default:
      color = "default";
      label = String(status) || '알수없음';
  }
  return <Chip label={label} color={color} size="small" sx={{ minWidth: 70 }}/>;
};

const initialFormData: WearableFormData = {
  pool_id: 0,
  device_id: '',
  device_type: 'wristband',
  status: 'active',
  battery_level: 100,
  firmware_version: '1.0.0',
  is_active: true,
  name: '',
  last_battery_level: 0,
  last_signal_strength: 0,
  code: '',
  usage_purpose: DEFAULT_WEARABLE_USAGE_PURPOSE,
};

const renderUsagePurposeChip = (purpose?: string) => {
  const p = normalizeWearableUsagePurpose(purpose);
  const label = wearableUsagePurposeLabel(p);
  return (
    <Chip
      label={label}
      size="small"
      color={p === 'safety_alert' ? 'warning' : 'info'}
      variant={p === 'safety_alert' ? 'filled' : 'outlined'}
      sx={{ minWidth: 72 }}
    />
  );
};

const WearableManagement: React.FC<WearableManagementProps> = ({
  poolId,
  poolCode,
  showSnackbar,
  onRefreshData
}) => {
  // 상태 관리
  const [wearables, setWearables] = useState<Wearable[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // 웨어러블 다이얼로그 상태
  const [isWearableDialogOpen, setIsWearableDialogOpen] = useState<boolean>(false);
  const [editingWearable, setEditingWearable] = useState<Wearable | null>(null);
  const [wearableFormData, setWearableFormData] = useState<WearableFormData>(initialFormData);
  const [wearableFormErrors, setWearableFormErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // 최신 바이오리듬 데이터 팝업 상태
  const [bioDialogOpen, setBioDialogOpen] = useState<boolean>(false);
  const [selectedBioWearable, setSelectedBioWearable] = useState<Wearable | null>(null);
  const [latestBio, setLatestBio] = useState<Record<string, any> | null>(null);
  const [isBioLoading, setIsBioLoading] = useState<boolean>(false);

  // 웨어러블 데이터 로드
  const loadWearableData = async () => {
    if (!poolId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const requestUrl = `/api/pools/${poolId}/wearables`;
      console.log('API 요청 URL:', requestUrl);
      
      const response = await api.get(requestUrl);
      console.log('API 응답 데이터:', response.data);
      
      if (response.data) {
        const validWearables = Array.isArray(response.data) ? response.data.map((wearable: any) => {
          // API 응답 그대로 사용하되, 부족한 필드는 기본값 제공
          const processedWearable: Wearable = {
            id: wearable.id,
            pool_id: wearable.pool_id,
            device_id: wearable.device_id || '',
            device_type: wearable.device_type || 'wristband',
            battery_level: typeof wearable.battery_level === 'number' ? wearable.battery_level : 0,
            // API에서 제공되지 않는 필드는 기본값 제공
            status: wearable.status || 'active',
            firmware_version: wearable.firmware_version || '',
            is_active: wearable.is_active !== false,
            created_at: wearable.created_at,
            updated_at: wearable.updated_at,
            name: wearable.name || '',
            last_battery_level: wearable.last_battery_level || 0,
            last_signal_strength: wearable.last_signal_strength || 0,
            code: wearable.code || '',
            usage_purpose: normalizeWearableUsagePurpose(wearable.usage_purpose),
          };
          
          return processedWearable;
        }) : [];
        
        setWearables(validWearables);
      } else {
        console.log('API 응답에 데이터가 없음');
        setWearables([]);
      }
    } catch (error: any) {
      console.error('웨어러블 데이터 로드 중 예외 발생:', error);
      let errorMessage = '웨어러블 데이터를 불러오는 중 오류 발생';
      
      if (error.response) {
        errorMessage = `서버 오류: ${error.response.status} - ${error.response.statusText}`;
        console.error('서버 응답:', error.response);
      } else if (error.request) {
        errorMessage = '서버에 연결 불가. 서버가 실행 중인지 확인';
        console.error('요청 정보:', error.request);
      } else {
        console.error('에러 메시지:', error.message);
      }
      
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 최신 바이오리듬 데이터 조회
  const handleFetchLatestBio = async (wearable: Wearable) => {
    if (!poolId || !wearable.id) return;
    setSelectedBioWearable(wearable);
    setBioDialogOpen(true);
    setIsBioLoading(true);
    setLatestBio(null);
    try {
      const response = await api.get(`/pools/${poolId}/wearables/${wearable.id}/sensor-data/latest`);
      setLatestBio(response.data || null);
    } catch (error: any) {
      console.error('바이오 데이터 조회 실패:', error);
      setLatestBio({ empty: true, error: '바이오 데이터를 불러오기 실패' });
    } finally {
      setIsBioLoading(false);
    }
  };

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    if (poolId) {
      loadWearableData();
    }
  }, [poolId]);

  // 웨어러블 다이얼로그 열기
  const handleOpenWearableDialog = (wearable?: Wearable) => {
    // 수정 모드
    if (wearable) {
      console.log('웨어러블 수정 모드 열기:', wearable);
      
      // 상태값 처리 - 문자열로 확실하게 변환하고 유효한 값이 아니면 기본값 사용
      let statusValue = String(wearable.status || '').toLowerCase();
      const validStatuses = ['active', 'inactive', 'maintenance', 'lost', 'error'];
      
      if (!validStatuses.includes(statusValue)) {
        console.warn(`유효하지 않은 상태값: ${statusValue}, 기본값 'active'로 설정합니다.`);
        statusValue = 'active';
      }
      
      console.log('설정된 상태값:', statusValue);
      
      setEditingWearable(wearable);
      setWearableFormData({
        pool_id: wearable.pool_id,
        device_id: wearable.device_id || '',
        device_type: wearable.device_type || 'wristband',
        status: statusValue,
        battery_level: wearable.battery_level,
        firmware_version: wearable.firmware_version || '',
        is_active: wearable.is_active !== false,
        name: wearable.name || '',
        last_battery_level: wearable.last_battery_level || 0,
        last_signal_strength: wearable.last_signal_strength || 0,
        code: wearable.code || '',
        usage_purpose: normalizeWearableUsagePurpose(wearable.usage_purpose),
      });
    } 
    // 추가 모드
    else {
      setEditingWearable(null);
      setWearableFormData({
        pool_id: Number(poolId) || 0,
        device_id: '',
        device_type: 'wristband',
        status: 'active',
        battery_level: 100,
        firmware_version: '1.0.0',
        is_active: true,
        name: '',
        last_battery_level: 0,
        last_signal_strength: 0,
        code: '',
        usage_purpose: DEFAULT_WEARABLE_USAGE_PURPOSE,
      });
    }
    
    setWearableFormErrors({});
    setIsWearableDialogOpen(true);
  };

  // 웨어러블 다이얼로그 닫기
  const handleCloseWearableDialog = () => {
    setIsWearableDialogOpen(false);
    setEditingWearable(null);
    setWearableFormData(initialFormData);
    setWearableFormErrors({});
  };

  // 웨어러블 폼 필드 변경 핸들러
  const handleWearableChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const target = e.target as HTMLInputElement;
    const { name, value } = target;

    if (name) {
      if (target.type === 'checkbox' && name === 'is_active') {
        // 활성화 상태 명확히 매칭
        setWearableFormData(prev => ({ ...prev, is_active: target.checked }));
      } else if (target.type === 'number') {
        setWearableFormData(prev => ({ ...prev, [name]: Number(value) }));
      } else {
        setWearableFormData(prev => ({ ...prev, [name]: value }));
      }

      // 에러 필드 클리어
      if (wearableFormErrors[name]) {
        setWearableFormErrors(prev => ({ ...prev, [name]: false }));
      }
    }
  };

  // 웨어러블 폼 셀렉트 필드 변경 핸들러
  const handleWearableSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    
    // 상태 필드 매칭 (명확한 매핑)
    if (name === 'status') {
      console.log(`상태값 변경: ${value}`);
      
      // 선택된 옵션에 해당하는 영문 상태값 찾기
      const selectedOption = WEARABLE_STATUS_OPTIONS.find(option => option.value === value);
      
      if (selectedOption) {
        const statusEnValue = selectedOption.enValue;
        console.log(`상태값 변환: ${value} (${selectedOption.label}) -> ${statusEnValue}`);
        
        // 찾은 영문 상태값으로 폼 데이터 업데이트
        setWearableFormData(prev => ({ ...prev, status: statusEnValue }));
      } else {
        // 매칭되는 옵션이 없는 경우, value 그대로 사용
        console.warn(`알 수 없는 상태값: ${value} - 그대로 사용합니다.`);
        setWearableFormData(prev => ({ ...prev, status: value }));
      }
    } else {
      // 다른 필드는 그대로 설정
      setWearableFormData(prev => ({ ...prev, [name]: value }));
    }
    
    // 에러 필드 클리어
    if (wearableFormErrors[name as string]) {
      setWearableFormErrors(prev => ({ ...prev, [name as string]: false }));
    }
  };

  // 웨어러블 폼 유효성 검사
  const validateWearableForm = (): boolean => {
    const errors: Record<string, boolean> = {};
    
    // 기본 유효성 검사
    if (!wearableFormData.device_id) errors.device_id = true;
    if (!wearableFormData.device_type) errors.device_type = true;
    
    setWearableFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // 웨어러블 제출 (추가/수정)
  const handleWearableSubmit = async () => {
    if (!validateWearableForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const wearableData = {
        ...wearableFormData,
        pool_id: Number(poolId)
      };

      if (editingWearable) {
        // 웨어러블 업데이트
        await api.put(`/api/pools/${poolId}/wearables/${editingWearable.id}`, wearableData);
        showSnackbar('웨어러블이 수정됨', 'success');
      } else {
        // 웨어러블 생성
        await api.post(`/api/pools/${poolId}/wearables`, wearableData);
        showSnackbar('웨어러블이 등록 완료', 'success');
      }

      // 다이얼로그 닫기 및 데이터 새로고침
      handleCloseWearableDialog();
      loadWearableData();
      if (onRefreshData) {
        onRefreshData();
      }
    } catch (error) {
      console.error('웨어러블 저장 중 오류 발생:', error);
      showSnackbar('웨어러블 저장 중 오류 발생', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 웨어러블 삭제
  const handleDeleteWearable = async (wearableId: number) => {
    if (!window.confirm('정말로 이 웨어러블을 삭제 확인')) return;
    
    try {
      await api.delete(`/api/pools/${poolId}/wearables/${wearableId}`);
      showSnackbar('웨어러블이 삭제 완료', 'success');
      loadWearableData(); // 목록 새로고침
      if (onRefreshData) {
        onRefreshData(); // 부모 컴포넌트 새로고침 콜백 호출
      }
    } catch (error: any) {
      console.error('웨어러블 삭제 중 에러:', error);
      let errorMessage = '웨어러블 삭제 중 오류 발생';
      
      if (error.response) {
        errorMessage = `서버 오류: ${error.response.status} - ${error.response.statusText}`;
      } else if (error.request) {
        errorMessage = '서버에 연결 불가';
      }
      
      showSnackbar(errorMessage, 'error');
    }
  };

  // 웨어러블 목록 렌더링
  const renderWearables = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">
            <CircularProgress size={24} />
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">
            <Typography color="error">{error}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (wearables.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={10} align="center">
            <Typography>착용기기 없음.</Typography>
          </TableCell>
        </TableRow>
      );
    }

    // device_id 기준으로 정렬
    const sortedWearables = [...wearables].sort((a, b) => 
      a.device_id.localeCompare(b.device_id)
    );

    return sortedWearables.map((wearable) => (
      <TableRow key={wearable.id}>
        <TableCell>{wearable.device_id}</TableCell>
        <TableCell>{wearable.name || '이름 없음'}</TableCell>
        <TableCell>{wearable.code || '-'}</TableCell>
        <TableCell>{getWearableTypeDisplay(wearable.device_type)}</TableCell>
        <TableCell>{renderUsagePurposeChip(wearable.usage_purpose)}</TableCell>
        <TableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              '& .MuiLinearProgress-root': {
                flexGrow: 1,
                mx: 1,
              },
            }}
          >
            {renderBatteryIcon(wearable.battery_level)}
            <LinearProgress
              variant="determinate"
              value={wearable.battery_level}
              color={getBatteryColor(wearable.battery_level)}
            />
            <Typography variant="body2">{`${wearable.battery_level}%`}</Typography>
          </Box>
        </TableCell>
        <TableCell>{wearable.firmware_version || '-'}</TableCell>
        <TableCell>
          <Chip 
            label={`상태: ${getStatusText(wearable.status)}`}
            color={getStatusColor(wearable.status)}
            size="small"
            sx={{ minWidth: 80 }}
          />
        </TableCell>
        <TableCell>
          <Chip 
            label={`활성화: ${wearable.is_active ? 'True' : 'False'}`} 
            color={wearable.is_active ? 'primary' : 'default'} 
            size="small" 
            sx={{ minWidth: 80 }}
          />
        </TableCell>
        <TableCell>
          <ButtonGroup size="small" aria-label="wearable actions">
            <Tooltip title="편집">
              <Button
                onClick={() => handleOpenWearableDialog(wearable)}
                color="primary"
              >
                <EditIcon fontSize="small" />
              </Button>
            </Tooltip>
            <Tooltip title="최신 바이오">
              <Button
                onClick={() => handleFetchLatestBio(wearable)}
                color="info"
              >
                Bio
              </Button>
            </Tooltip>
            <Tooltip title="삭제">
              <Button
                onClick={() => handleDeleteWearable(wearable.id)}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </Button>
            </Tooltip>
          </ButtonGroup>
        </TableCell>
      </TableRow>
    ));
  };

  // 웨어러블 추가/수정 다이얼로그 렌더링
  const renderWearableDialog = () => {
    return (
      <Dialog open={isWearableDialogOpen} onClose={handleCloseWearableDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingWearable ? '웨어러블 정보 수정' : '웨어러블 추가'}
          <IconButton
            aria-label="close"
            onClick={handleCloseWearableDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="device_id"
                label="장치 ID"
                placeholder="장치 ID를 입력"
                value={wearableFormData.device_id}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
                required
                error={wearableFormErrors.device_id}
                helperText={
                  wearableFormErrors.device_id
                    ? "장치 ID는 필수 입력 항목"
                    : "이 시설 안에서만 유일. 다른 시설과 같은 ID를 써도 됨."
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="code"
                label="장치 코드"
                placeholder="장치 코드를 입력 (예: POOL001-WB001)"
                value={wearableFormData.code}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
                required
                error={wearableFormErrors.code}
                helperText={wearableFormErrors.code ? "장치 코드 필수 입력 항목" : ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="장치 이름"
                placeholder="장치 이름을 입력"
                value={wearableFormData.name || ''}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required error={wearableFormErrors.device_type}>
                <InputLabel id="wearable-type-label">타입</InputLabel>
                <Select
                  labelId="wearable-type-label"
                  name="device_type"
                  value={wearableFormData.device_type || ''}
                  onChange={handleWearableSelectChange}
                  label="타입"
                >
                  {WEARABLE_TYPE_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {wearableFormErrors.device_type && <FormHelperText>타입 선택</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="wearable-usage-label">용도</InputLabel>
                <Select
                  labelId="wearable-usage-label"
                  name="usage_purpose"
                  value={wearableFormData.usage_purpose || DEFAULT_WEARABLE_USAGE_PURPOSE}
                  onChange={handleWearableSelectChange}
                  label="용도"
                >
                  {WEARABLE_USAGE_PURPOSE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="wearable-status-label">상태</InputLabel>
                <Select
                  labelId="wearable-status-label"
                  name="status"
                  value={getStatusSelectValue(wearableFormData.status)}
                  onChange={handleWearableSelectChange}
                  label="상태"
                >
                  {WEARABLE_STATUS_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>웨어러블의 현재 작동 상태 (활성, 비활성, 점검중, 분실, 오류)</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="battery_level"
                label="배터리 레벨"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={wearableFormData.battery_level}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
                inputProps={{
                  min: 0,
                  max: 100,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="firmware_version"
                label="펌웨어 버전"
                placeholder="펌웨어 버전을 입력"
                value={wearableFormData.firmware_version || ''}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="last_battery_level"
                label="마지막 배터리 레벨"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                value={wearableFormData.last_battery_level || 0}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
                inputProps={{
                  min: 0,
                  max: 100,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="last_signal_strength"
                label="마지막 신호 강도"
                type="number"
                InputProps={{
                  endAdornment: <InputAdornment position="end">dBm</InputAdornment>,
                }}
                value={wearableFormData.last_signal_strength || 0}
                onChange={handleWearableChange}
                fullWidth
                margin="normal"
                inputProps={{
                  min: -100,
                  max: 0,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={wearableFormData.is_active ?? true}
                    onChange={handleWearableChange}
                    name="is_active"
                    color="primary"
                  />
                }
                label="시스템 활성화 여부"
              />
              <FormHelperText>
                웨어러블을 시스템에서 활성화 또는 비활성화
                이 설정은 상태값과는 별개로 웨어러블이 시스템에서 사용 가능한지를 결정.
              </FormHelperText>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseWearableDialog}>취소</Button>
          <Button 
            onClick={handleWearableSubmit} 
            variant="contained" 
            color="primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : (editingWearable ? '수정' : '추가')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // 최신 바이오리듬 데이터 다이얼로그
  const renderBioDialog = () => {
    const bio = latestBio || { empty: true };
    const fields = [
      { key: 'heart_rate', label: '심박수 (bpm)' },
      { key: 'oxygen_saturation', label: 'SpO2 (%)' },
      { key: 'body_temperature', label: '체온 (℃)' },
      { key: 'respiration_rate', label: '호흡수 (회/분)' },
      { key: 'fatigue_score', label: '피로도' },
      { key: 'stress_score', label: '스트레스' },
      { key: 'hrv', label: 'HRV (ms)' },
      { key: 'sleep_score', label: '수면 점수' },
      { key: 'activity_level', label: '활동량' },
      { key: 'battery_level', label: '배터리 (%)' },
      { key: 'signal_strength', label: '신호 세기' },
    ];
    return (
      <Dialog open={bioDialogOpen} onClose={() => setBioDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          최신 바이오리듬
          {selectedBioWearable && ` — ${selectedBioWearable.device_id}`}
          <IconButton
            aria-label="close"
            onClick={() => setBioDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isBioLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : bio.empty ? (
            <Typography>최신 바이오 데이터 없음.</Typography>
          ) : (
            <Grid container spacing={2}>
              {fields.map((f) => (
                <Grid item xs={6} key={f.key}>
                  <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {f.label}
                    </Typography>
                    <Typography variant="h6">
                      {bio[f.key] !== undefined && bio[f.key] !== null ? bio[f.key] : '-'}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  측정 시각: {bio.timestamp ? new Date(bio.timestamp).toLocaleString() : '-'}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBioDialogOpen(false)}>닫기</Button>
          {selectedBioWearable && (
            <Button
              onClick={() => handleFetchLatestBio(selectedBioWearable)}
              variant="outlined"
              disabled={isBioLoading}
            >
              새로고침
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // 메인 렌더링
  return (
    <Box sx={{ mt: 2 }}>
      {/* —— 착용기기 목록 —— */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" component="h2">
              웨어러블 관리
            </Typography>
            <Typography variant="body2" color="text.secondary">
              등록된 착용기기 목록
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenWearableDialog()}
          >
            웨어러블 추가
          </Button>
        </Box>

        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            <strong>주의사항:</strong> 웨어러블 관리에서는 두 가지 상태 정보를 구분하여 관리:
            <br />
            1) <strong>상태(status)</strong>: 웨어러블의 작동 상태 (활성, 비활성, 점검중, 분실, 오류 등)
            <br />
            2) <strong>활성화 여부(is_active)</strong>: 웨어러블이 시스템에서 사용 가능한지 여부 (활성화/비활성화)
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>장치 ID</TableCell>
                <TableCell>장치이름</TableCell>
                <TableCell>장치 코드</TableCell>
                <TableCell>종류</TableCell>
                <TableCell>용도</TableCell>
                <TableCell>배터리</TableCell>
                <TableCell>펌웨어</TableCell>
                <TableCell>상태(Status)</TableCell>
                <TableCell>시스템 활성화(Active)</TableCell>
                <TableCell>액션</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderWearables()}</TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Divider sx={{ my: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
          생체·바이오리듬 기준값
        </Typography>
      </Divider>

      <Box sx={{ mt: 2 }}>
        <PoolWearableBaselineDefaultsCard poolId={poolId} showSnackbar={showSnackbar} />
      </Box>

      <Divider sx={{ my: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
          연동·게이트웨이 설정
        </Typography>
      </Divider>

      <Box sx={{ mt: 2 }}>
        <PoolWearableGatewaySettingsCard poolId={poolId} showSnackbar={showSnackbar} />
      </Box>

      {renderWearableDialog()}
      {renderBioDialog()}
    </Box>
  );
};

export default WearableManagement; 